import React from 'react';
import { CancelToken } from 'axios';
import { ErrorDisplay } from '@components/error/error.display';
import { Loading } from '@components/loading';
import { useAuth } from '@domain/auth/auth.hook';
import { useFormData } from '@domain/hooks/use.form.data';
import { PlayerInfoData } from '@domain/types/player/player.info.data';
import { AccountInfo } from '@screens/players/player.details.tabs/info/account.info';
import { InfoAreaContainer } from '@screens/players/player.details.tabs/player.info.styles';
import { PlayerDataProps } from '@screens/players/types/player.data.props';
import { ContactInfo } from '@screens/players/player.details.tabs/info/contact.info';
import { VipCompleteInfo } from '@screens/players/player.details.tabs/info/vip.complete.info';
import { DailyBonusInfo } from '@screens/players/player.details.tabs/info/daily.bonus.info';
import { FreeSpinInfo } from '@screens/players/player.details.tabs/info/free.spin.info';
import { LuckySpinInfo } from '@screens/players/player.details.tabs/info/lucky.spin.info';
import { DogCosmeticsInfo } from '@screens/players/player.details.tabs/info/dog.cosmetics.info';
import { GameType } from '@domain/game.selection/types/game.type';
import { useGame } from '@domain/game.selection/game.selection.hook';
import { RexDailyBonusInfo } from '@screens/players/player.details.tabs/info/rex.daily.bonus.info';

const PlayerInfo = ({ player }: PlayerDataProps) => {
  const { authorizedServices } = useAuth();
  const { pid } = player;

  async function runSearch(requestPid: number, cancelToken: CancelToken | undefined) {
    const response = await authorizedServices
      .get<PlayerInfoData>(`/admin/player/info/${requestPid}`, { cancelToken })
      .catch((error) => {
        if (error && error.response && error.response.status === 404) throw new Error('No data found');
        throw error;
      });
    return response.data;
  }

  const formData = useFormData(pid, runSearch, { autoRequest: true });

  const { response: playerInfo, loading, error } = formData;

  const game = useGame();

  if (loading) return <Loading loading />;
  if (error) return <ErrorDisplay error={error} />;
  if (!playerInfo) return null;

  return (
    <>
      <InfoAreaContainer>
        <AccountInfo player={player} info={playerInfo} />
        <ContactInfo player={player} info={playerInfo} />
        <VipCompleteInfo player={player} info={playerInfo} />

        {game.type === GameType.Rex ? <RexDailyBonusInfo player={player} info={playerInfo} /> : <DailyBonusInfo player={player} info={playerInfo} />}

        <FreeSpinInfo player={player} info={playerInfo} />
        <LuckySpinInfo player={player} info={playerInfo} />
        <DogCosmeticsInfo player={player} info={playerInfo} />
      </InfoAreaContainer>
    </>
  );
};

export default PlayerInfo;
